import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

interface OverlayProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  zIndex?: string;
  children: ReactNode;
}

export function Overlay({
  isOpen,
  onClose,
  className,
  zIndex = "z-[100]",
  children,
}: OverlayProps) {
  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Handle body overflow
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4",
        zIndex,
        className
      )}
      onClick={onClose}
    >
      {children}
    </div>,
    document.body
  );
}
